import { useCallback, useEffect, useState } from 'react';
import {
  CircularProgress,
  Grid,
  TextField,
} from '@material-ui/core';
import { CategoryApi, CompanyApi } from 'services';
import { isNumeric } from 'utils';
import { POSTAL_CODE_REGEX, SIREN_REGEX, streetTypeList } from 'config/constants';
import { useFormik } from 'formik';
import AddressFields from '../AddressFields';
import DefaultButton from 'components/DefaultButton';
import DefaultMenuItem from 'components/DefaultMenuItem';
import DocumentCard from 'components/DocumentCard';
import kbisLogo from '_assets/register/kbis-logo.svg';
import useStyles from './styles';
import * as Yup from 'yup';
import _ from 'lodash';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 0;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: "13%",
      minWidth: "13%",
    },
  },
};

const validationSchema = Yup.object({
  activity: Yup
    .string()
    .required('Champ obligatoire'),
  address_name: Yup
    .string()
    .required('Champ obligatoire'),
  category: Yup.string(),
  city_name: Yup
    .string()
    .required('Champ obligatoire'),
  siren: Yup
    .string()
    .matches(SIREN_REGEX, 'Veuillez saisir un numéro de siren valide')
    .required('Champ obligatoire'),
  social_name: Yup
    .string()
    .required('Champ obligatoire'),
  street_index: Yup.string(),
  street_number: Yup
    .number()
    .required('Champ obligatoire'),
  subcategory: Yup.string(),
  zip_code: Yup
    .string()
    .matches(POSTAL_CODE_REGEX, 'Veuillez saisir un code postal valide')
    .required('Champ obligatoire'),
});

function CompanyForm({
  autoSaveDocument = false,
  company,
  disabled,
  enableDocument,
  onCancel = () => {},
  onDocumentChange = () => {},
  onSubmit = () => {},
}) {
  const classes = useStyles();

  const {
    errors,
    handleChange,
    handleSubmit,
    isSubmitting,
    resetForm,
    setFieldValue,
    setValues,
    touched,
    values
  } = useFormik({
    initialValues: { ...company },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (autoSaveDocument) await onSubmit(values);
      else {
        const { documentUpdated } = await onSubmit(values, { ...document });
        setDocument({ file: null, url: documentUpdated ? document.url : company?.kbisURL ?? null });
      }
    },
  });

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [activities, setActivities] = useState([]);
  const [categoriesDictionary, setCategoriesDictionary] = useState([]);
  const [document, setDocument] = useState({ file: null, url: company?.kbisURL ?? null });
  const [isUpdatingDocument, setIsUpdatingDocument] = useState(false);

  useEffect(() => {
    getCategories();
  }, []);

  const getCategoryFromActivity = useCallback((currentComp) => {
    categoriesDictionary.forEach(el => {
      let res = el.children.map(e => {
        if (e.level === 2 && e.name === currentComp?.activity) return true;

        let res = e.children.map((l) => l?.name === currentComp?.activity);

        if (res.includes(true)) {
          setActivities(e.children.map(o => o.name));
          setFieldValue('subcategory', e.name);
          return true;
        }

        return false;
      });

      if (res.includes(true)) {
        if (el.children[0].level === 2) setActivities(el.children.map(p => p.name));
        else setSubcategories(el.children.map(p => p.name));
        setFieldValue('category', el.name);
      }
    });
  }, [categoriesDictionary, setFieldValue]);

  useEffect(() => {
    getCategoryFromActivity(company);
  }, [categoriesDictionary, company, getCategoryFromActivity]);

  useEffect(() => {
    resetForm();
    setValues(company);
    setDocument({ file: null, url: company?.kbisURL ?? null })

    setActivities([]);
    setSubcategories([]);
    getCategoryFromActivity(company);
  }, [company, getCategoryFromActivity, resetForm, setValues]);

  const getCategories = () => {
    CategoryApi.listCategories()
      .then(({ categories }) => {
        setCategories(categories.map(category => category.name));
        setCategoriesDictionary(categories);
      })
      .catch(() => { });
  }

  const handleSirenChange = ({ target: { value } }) => {
    setFieldValue('siren', value);

    const siren = value.split(" ").join("");
    if (siren.length === 9 && isNumeric(siren)) {
      CompanyApi.getCompanyInformationBySiren(siren)
        .then(({ companyInfos }) => {
          const wayType = streetTypeList.find(t => t.value === companyInfos.street_type).label;
          setValues( {
            ...values, ...{
              address_name: `${wayType} ${companyInfos.address_name}`,
              city_name: companyInfos.city,
              siren: value,
              social_name: companyInfos.socialName,
              street_number: companyInfos.street_number,
              zip_code: companyInfos.zip_code,
            }
          }, false);
        });
    }
  }

  const handleCityChange = (event, value) => {
    setFieldValue('city_name', value);
  }

  const handleAddressNameChange = (event, value) => {
    setFieldValue('address_name', value);
  }

  const handleCategoryChange = ({ target: { value } }) => {
    setFieldValue('category', value);
    setFieldValue('subcategory', '');
    setFieldValue('activity', '');

    let subcategoriesDict = null;
    if (_.filter(categoriesDictionary, { name: value })[0].children[0].level === 1) {
      subcategoriesDict = _.filter(categoriesDictionary, { name: value })[0].children;
      let subcategoriesNames = subcategoriesDict.map((subcategory) => subcategory.name);
      setSubcategories(subcategoriesNames);
    } else {
      let activitiesDict = _.filter(categoriesDictionary, { name: value })[0].children;
      let activitiesNames = activitiesDict.map((activity) => activity.name);
      setActivities(activitiesNames);
      setSubcategories([]);
    }

    if (subcategoriesDict != null) {
      let firstSubcategoryFound = _.filter(subcategoriesDict, { name: value })[0];
      if (firstSubcategoryFound != null) {
        let activitiesDict = firstSubcategoryFound.children;
        let activitiesNames = activitiesDict.map((activity) => activity.name);
        setActivities(activitiesNames);
      } else {
        setActivities([]);
      }
    }
  }

  const handleSubCategoryChange = ({ target: { value } }) => {
    setFieldValue('subcategory', value);

    let subcategoriesDict = _.filter(categoriesDictionary, { name: values.category })[0].children;
    let activitiesDict = _.filter(subcategoriesDict, { name: value })[0].children;
    let activitiesNames = activitiesDict.map((activity) => activity.name);
    setActivities(activitiesNames);
  }

  const handleDocumentChange = async (file, url) => {
    if (autoSaveDocument) {
      setIsUpdatingDocument(true);
      const updated = await onDocumentChange({ file, url });
      setDocument({ file: null, url: updated ? url : company?.kbisURL ?? null });
      setIsUpdatingDocument(false);
    } else {
      setDocument({ file, url });
    }
  }

  const onCancelFormClick = () => {
    if (isSubmitting) return;

    onCancel();
    resetForm();
    setDocument({ file: null, url: company?.kbisURL ?? null });
    getCategoryFromActivity(company);
  }

  return (
    <form
      autoComplete="off"
      className={classes.root}
      onSubmit={handleSubmit}
    >
      <Grid container justifyContent='center' style={{ height: '100%' }}>
        <Grid container item xs={12} md={9} alignItems='center' justifyContent='center' direction='column' spacing={3} style={{ flexGrow: 1 }}>
          <Grid container item>
            <TextField
              disabled={disabled}
              fullWidth
              label='Numéro de SIREN'
              name='siren'
              variant='outlined'
              value={values.siren}
              onChange={handleSirenChange}
              error={touched.siren && Boolean(errors.siren)}
              helperText={touched.siren && errors.siren}
            />
          </Grid>
          <Grid container item>
            <TextField
              disabled={disabled}
              fullWidth
              label='Raison Social'
              name='social_name'
              variant='outlined'
              value={values.social_name}
              onChange={handleChange}
              error={touched.social_name && Boolean(errors.social_name)}
              helperText={touched.social_name && errors.social_name}
            />
          </Grid>

          <Grid container item>
            <AddressFields
              disabled={disabled}
              errors={errors}
              handleAddressNameChange={handleAddressNameChange}
              handleChange={handleChange}
              handleCityChange={handleCityChange}
              touched={touched}
              values={values}
            />
          </Grid>

          <Grid container item>
            <Grid container item spacing={3}>
              <Grid item xs={4}>
                <TextField
                  disabled={disabled}
                  fullWidth
                  select
                  label='Catégorie'
                  name='category'
                  variant='outlined'
                  value={values.category}
                  onChange={handleCategoryChange}
                  error={touched.activity && Boolean(errors.activity)}
                >
                  {categories.map((currentCategory) => (
                    <DefaultMenuItem className={classes.menuItem} key={currentCategory} value={currentCategory}>{currentCategory}</DefaultMenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={4}>
                <TextField
                  disabled={subcategories.length === 0 || disabled}
                  fullWidth
                  select
                  label='Sous-catégorie'
                  name='subcategory'
                  variant='outlined'
                  value={values.subcategory}
                  onChange={handleSubCategoryChange}
                  error={touched.activity && Boolean(errors.activity)}
                  SelectProps={{
                    MenuProps: MenuProps,
                  }}
                >
                  {subcategories.map((currentSubcategory) => (
                    <DefaultMenuItem className={classes.menuItem} key={currentSubcategory} value={currentSubcategory}>{currentSubcategory}</DefaultMenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={4}>
                <TextField
                  disabled={activities.length === 0 || disabled}
                  fullWidth
                  select
                  label='Activité'
                  name='activity'
                  variant='outlined'
                  value={values.activity}
                  onChange={handleChange}
                  error={touched.activity && Boolean(errors.activity)}
                  SelectProps={{
                    MenuProps: MenuProps,
                  }}
                >
                  {activities.map((currentActivity) => (
                    <DefaultMenuItem className={classes.menuItem} key={currentActivity} value={currentActivity}>{currentActivity}</DefaultMenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Grid>

          {!!enableDocument &&
          <div style={{ margin: "0 0 3vh 0" }}>
            <DocumentCard
              disabled={(!autoSaveDocument && disabled) || isUpdatingDocument}
              document={document}
              logo={kbisLogo}
              name='KBIS'
              onChange={handleDocumentChange}
            />
          </div>
          }
        </Grid>

        {!disabled &&
          <Grid container item xs={12} justifyContent='center'>
            <Grid container item xs={12} alignItems='flex-end' justifyContent='center' style={{ marginBottom: '16px' }}>
              <DefaultButton cancel onClick={onCancelFormClick}>
                Annuler
              </DefaultButton>
              <DefaultButton color='secondary' disabled={isSubmitting} type='submit' variant='contained'>
                {isSubmitting ? <CircularProgress size={20} /> : 'Valider'}
              </DefaultButton>
            </Grid>
          </Grid>
        }
      </Grid>
    </form>
  )
}

export default CompanyForm;
